/**
 * Performance monitoring utility for critical operations
 * Tracks and logs operation timings with thresholds
 * 
 * Light QA Contract Integration:
 * - Core Web Vitals tracking (LCP, INP, CLS)
 * - Error rate monitoring
 * - Latency P95 calculation
 */

import { logger } from './logger';
import { coreWebVitals } from './coreWebVitals';

interface PerformanceThresholds {
  warning: number;
  critical: number;
}

interface PerformanceMetrics {
  operation: string;
  duration: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

class PerformanceMonitor {
  private activeTimers = new Map<string, number>();
  private metrics: PerformanceMetrics[] = [];
  private maxMetricsHistory = 100;
  
  // Default thresholds in milliseconds
  private defaultThresholds: PerformanceThresholds = {
    warning: 1000,   // 1 second
    critical: 3000   // 3 seconds
  };
  
  // Operation-specific thresholds
  private operationThresholds = new Map<string, PerformanceThresholds>([
    ['query', { warning: 500, critical: 2000 }],
    ['mutation', { warning: 1000, critical: 3000 }],
    ['file_upload', { warning: 2000, critical: 5000 }],
    ['page_load', { warning: 1500, critical: 4000 }],
    ['component_render', { warning: 100, critical: 500 }]
  ]);

  /**
   * Start timing an operation
   */
  startTimer(operationId: string): void {
    this.activeTimers.set(operationId, performance.now());
    logger.debug(`Performance: Started timing ${operationId}`);
  }

  /**
   * End timing and log performance metrics
   */
  endTimer(
    operationId: string, 
    operationType: string = 'operation',
    metadata?: Record<string, any>
  ): number {
    const startTime = this.activeTimers.get(operationId);
    if (!startTime) {
      logger.warn(`Performance: No start time found for ${operationId}`);
      return 0;
    }

    const duration = performance.now() - startTime;
    this.activeTimers.delete(operationId);

    const metrics: PerformanceMetrics = {
      operation: operationId,
      duration,
      timestamp: Date.now(),
      metadata
    };

    this.addMetrics(metrics);
    this.logPerformance(operationId, operationType, duration, metadata);

    return duration;
  }

  /**
   * Time an async operation automatically
   */
  async timeOperation<T>(
    operationId: string,
    operationType: string,
    operation: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    this.startTimer(operationId);
    try {
      const result = await operation();
      this.endTimer(operationId, operationType, metadata);
      return result;
    } catch (error) {
      this.endTimer(operationId, operationType, { ...metadata, error: true });
      throw error;
    }
  }

  /**
   * Time a synchronous operation
   */
  timeSync<T>(
    operationId: string,
    operationType: string,
    operation: () => T,
    metadata?: Record<string, any>
  ): T {
    this.startTimer(operationId);
    try {
      const result = operation();
      this.endTimer(operationId, operationType, metadata);
      return result;
    } catch (error) {
      this.endTimer(operationId, operationType, { ...metadata, error: true });
      throw error;
    }
  }

  /**
   * Get performance report for recent operations
   */
  getPerformanceReport(): {
    recentMetrics: PerformanceMetrics[];
    slowOperations: PerformanceMetrics[];
    averageByOperation: Record<string, number>;
    webVitals?: ReturnType<typeof coreWebVitals.getSummary>;
  } {
    const recentMetrics = this.metrics.slice(-20);
    const slowOperations = this.metrics.filter(m => 
      m.duration > this.getThreshold(m.operation).warning
    );
    
    const averageByOperation = this.calculateAverages();

    // Include Core Web Vitals if available
    const webVitals = coreWebVitals.getSummary();

    return {
      recentMetrics,
      slowOperations,
      averageByOperation,
      webVitals
    };
  }

  private addMetrics(metrics: PerformanceMetrics): void {
    this.metrics.push(metrics);
    // Keep only recent metrics to prevent memory leaks
    if (this.metrics.length > this.maxMetricsHistory) {
      this.metrics = this.metrics.slice(-this.maxMetricsHistory);
    }
  }

  private logPerformance(
    operationId: string,
    operationType: string,
    duration: number,
    metadata?: Record<string, any>
  ): void {
    const thresholds = this.getThreshold(operationType);
    
    const logData = {
      operation: operationId,
      type: operationType,
      duration: Math.round(duration),
      thresholds,
      ...metadata
    };

    if (duration > thresholds.critical) {
      logger.error(`Performance CRITICAL: ${operationId} took ${Math.round(duration)}ms`, logData);
    } else if (duration > thresholds.warning) {
      logger.warn(`Performance WARNING: ${operationId} took ${Math.round(duration)}ms`, logData);
    } else {
      logger.debug(`Performance OK: ${operationId} completed in ${Math.round(duration)}ms`, logData);
    }
  }

  private getThreshold(operationType: string): PerformanceThresholds {
    return this.operationThresholds.get(operationType) || this.defaultThresholds;
  }

  private calculateAverages(): Record<string, number> {
    const operationGroups = new Map<string, number[]>();
    
    this.metrics.forEach(metric => {
      if (!operationGroups.has(metric.operation)) {
        operationGroups.set(metric.operation, []);
      }
      operationGroups.get(metric.operation)!.push(metric.duration);
    });

    const averages: Record<string, number> = {};
    operationGroups.forEach((durations, operation) => {
      const average = durations.reduce((sum, d) => sum + d, 0) / durations.length;
      averages[operation] = Math.round(average);
    });

    return averages;
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Convenience functions
export const startPerformanceTimer = (operationId: string) => 
  performanceMonitor.startTimer(operationId);

export const endPerformanceTimer = (
  operationId: string, 
  operationType: string = 'operation',
  metadata?: Record<string, any>
) => performanceMonitor.endTimer(operationId, operationType, metadata);

export const timeAsyncOperation = <T>(
  operationId: string,
  operationType: string,
  operation: () => Promise<T>,
  metadata?: Record<string, any>
) => performanceMonitor.timeOperation(operationId, operationType, operation, metadata);

export const timeSyncOperation = <T>(
  operationId: string,
  operationType: string,
  operation: () => T,
  metadata?: Record<string, any>
) => performanceMonitor.timeSync(operationId, operationType, operation, metadata);
